<template>
  <div v-if="usePublicLayout" class="public-layout-shell"><router-view /></div>
  <div v-else class="grid grid-cols-2-small app-screen overflow-hidden" :class="{ 'grid-cols-2-menu': activeParent }">
    <Nav ref="navigation" :mobile="isNarrow" :canInstall="!installed"
      :inert="isNarrow && !mobileMenuOpen ? '' : null"
      @navigate="closeMenu" @close="closeMenu" @install="requestInstallation" />
    <main class="app-main-shell overflow-hidden flex flex-col" :inert="isNarrow && mobileMenuOpen ? '' : null"
      :class="{ 'dashboard-bg': route.name === 'HomePage' }">
      <HeaderHome :menuExpanded="isNarrow ? mobileMenuOpen : !desktopCollapsed" @toggle-menu="toggleMenu" />
      <div id="app-status-region" class="shrink-0" />
      <div ref="content" class="app-router-shell flex-1 min-h-0 overflow-y-auto" tabindex="-1"><router-view /></div>
    </main>
  </div>
  <button v-if="!usePublicLayout && isNarrow && mobileMenuOpen" type="button" aria-label="Cerrar menú"
    class="app-menu-backdrop fixed inset-0 bg-black/40 z-40" tabindex="-1" @click="closeMenu" />
  <div id="container-pdf" />
  <InstallPWA :publicLayout="usePublicLayout" @installed="installed = true" />
</template>

<script>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Nav from './components/Nav/Nav.vue';
import HeaderHome from './components/Nav/Header.vue';
import InstallPWA from './components/PublicComponents/InstallPWA.vue';
import PWADetector from './utils/pwa/pwaDetector';
import { installViewportSync } from './utils/pwa/viewport';

export default {
  name: 'App',
  components: { Nav, HeaderHome, InstallPWA },
  setup() {
    const route = useRoute();
    const media = window.matchMedia('(max-width: 1023px)');
    const isNarrow = ref(media.matches), mobileMenuOpen = ref(false), desktopCollapsed = ref(false);
    const navigation = ref(null), content = ref(null), installed = ref(PWADetector.isPWAMode());
    const usePublicLayout = computed(() => route.name === 'login' || route.meta?.publicLayout);
    const activeParent = computed(() => isNarrow.value ? mobileMenuOpen.value : desktopCollapsed.value);
    let stopViewport;
    const closeMenu = () => {
      if (!mobileMenuOpen.value) return;
      mobileMenuOpen.value = false;
      nextTick(() => document.querySelector('[aria-controls="app-navigation"]')?.focus({ preventScroll: true }));
    };
    const toggleMenu = async () => {
      if (!isNarrow.value) { desktopCollapsed.value = !desktopCollapsed.value; return; }
      if (mobileMenuOpen.value) { closeMenu(); return; }
      mobileMenuOpen.value = true;
      await nextTick();
      navigation.value?.$el.querySelector('button, a')?.focus({ preventScroll: true });
    };
    const trapMenu = event => {
      if (!isNarrow.value || !mobileMenuOpen.value) return;
      if (event.key === 'Escape') { event.preventDefault(); closeMenu(); }
      if (event.key !== 'Tab') return;
      const controls = [...navigation.value.$el.querySelectorAll('button:not(:disabled), a[href], input')]
        .filter(element => element.getBoundingClientRect().height > 0 && getComputedStyle(element).visibility !== 'hidden');
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const requestInstallation = async () => {
      closeMenu(); await nextTick(); window.dispatchEvent(new CustomEvent('pwa:install-request'));
    };
    const resize = () => { isNarrow.value = media.matches; mobileMenuOpen.value = false; };
    watch(() => route.fullPath, () => { closeMenu(); if (content.value) content.value.scrollTop = 0; });
    onMounted(() => {
      stopViewport = installViewportSync(); media.addEventListener('change', resize);
      document.addEventListener('keydown', trapMenu);
    });
    onBeforeUnmount(() => {
      stopViewport?.(); media.removeEventListener('change', resize); document.removeEventListener('keydown', trapMenu);
    });
    return { route, usePublicLayout, navigation, content, isNarrow, mobileMenuOpen, desktopCollapsed,
      activeParent, installed, closeMenu, toggleMenu, requestInstallation };
  },
};
</script>

<style>
html, body, #app { min-height: 100%; height: 100%; }
body { overscroll-behavior: none; background-color: #f8fafc; }
.app-screen, .app-main-shell { height: var(--app-height, 100dvh); min-height: 0; }
.public-layout-shell { min-height: var(--app-height, 100dvh); width: 100%; overflow-x: hidden; overflow-y: auto; }
.grid-cols-2-small { grid-template-columns: 255px minmax(0, 1fr); }
.grid-cols-2-small.grid-cols-2-menu { grid-template-columns: 70px minmax(0, 1fr); }
.grid-cols-2-small.grid-cols-2-menu .small-hidden { display: none; }
.grid-cols-2-small.grid-cols-2-menu .small-center { justify-content: center; }
.dashboard-bg { background-color: #fafdff; }
.select { background-color: #008cff !important; color: #ffffff; }
input[type='number']::-webkit-inner-spin-button, input[type='number']::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
input[type='number'] { -moz-appearance: textfield; appearance: none; }
@media (max-width: 1023px) {
  .grid-cols-2-small, .grid-cols-2-small.grid-cols-2-menu { grid-template-columns: minmax(0, 1fr); }
  .grid-cols-2-small > nav {
    position: fixed; top: 0; left: 0; width: min(88vw, 336px);
    transform: translateX(-100%); visibility: hidden;
    transition: transform 180ms ease, visibility 180ms; z-index: 50;
  }
  .grid-cols-2-small.grid-cols-2-menu > nav { transform: translateX(0); visibility: visible; }
  .grid-cols-2-small.grid-cols-2-menu .small-hidden { display: inline-flex; }
  .grid-cols-2-small.grid-cols-2-menu .small-center { justify-content: flex-start; }
  .grid-cols-2-small.grid-cols-2-menu .toggle.small-center { justify-content: space-between; }
}
</style>
